// In-page Time Entry edit modal.
//
// Listens for clicks on `.te-edit-trigger` (which carry data-te-id), fetches
// the form HTML from /time_entries/:id/edit?modal=1, drops it into the
// dialog, and submits via fetch so the surrounding page never reloads. On
// success the user's filter state, scroll position, and view are preserved
// — we just refresh the data they're looking at.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["dialog", "body"]
  declare readonly dialogTarget: HTMLDialogElement
  declare readonly bodyTarget: HTMLElement

  connect(): void {
    document.addEventListener("click", this.delegateOpen)
    document.addEventListener("te:new", this.onNewRequest as EventListener)
  }
  disconnect(): void {
    document.removeEventListener("click", this.delegateOpen)
    document.removeEventListener("te:new", this.onNewRequest as EventListener)
  }

  // The day timeline dispatches this when an empty stretch is tapped,
  // carrying the wall-clock time the tap landed on.
  private onNewRequest = (event: CustomEvent<{ startedAt: string }>): void => {
    this.openNew(event.detail.startedAt)
  }

  private delegateOpen = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null
    const trigger = target?.closest(".te-edit-trigger") as HTMLElement | null
    if (!trigger) return
    const teId = trigger.dataset.teId
    if (!teId) return
    event.preventDefault()
    this.openFor(teId)
  }

  async openFor(teId: string): Promise<void> {
    await this.load(`/time_entries/${encodeURIComponent(teId)}/edit?modal=1`)
  }

  async openNew(startedAt: string): Promise<void> {
    await this.load(`/time_entries/new?modal=1&started_at=${encodeURIComponent(startedAt)}`)
  }

  private async load(url: string): Promise<void> {
    this.bodyTarget.innerHTML = `<div class="p-12 text-center text-sm text-[var(--color-ink-muted)]">Loading…</div>`
    this.showDialog()
    try {
      const res = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
        credentials: "same-origin",
      })
      const html = await res.text()
      this.bodyTarget.innerHTML = html
      this.bindForm()
    } catch (e) {
      this.bodyTarget.innerHTML = `<div class="p-6 text-sm text-[var(--color-state-red-500)]">Failed to load entry.</div>`
    }
  }

  close(): void {
    if (typeof this.dialogTarget.close === "function") this.dialogTarget.close()
    else this.dialogTarget.removeAttribute("open")
    this.bodyTarget.innerHTML = ""
  }

  // Backdrop click closes
  backdropClick(event: MouseEvent): void {
    if (event.target === this.dialogTarget) this.close()
  }

  private showDialog(): void {
    if (typeof this.dialogTarget.showModal === "function") this.dialogTarget.showModal()
    else this.dialogTarget.setAttribute("open", "")
  }

  // Hook the freshly-injected form: submit via fetch so the page stays put.
  private bindForm(): void {
    const form = this.bodyTarget.querySelector("form") as HTMLFormElement | null
    if (!form) return
    form.addEventListener("submit", async (e) => {
      e.preventDefault()
      const submit = form.querySelector("[type=submit]") as HTMLInputElement | HTMLButtonElement | null
      if (submit) submit.disabled = true
      const data = new FormData(form)
      try {
        const res = await fetch(form.action, {
          method: form.method.toUpperCase() === "GET" ? "GET" : "POST",
          body: data,
          credentials: "same-origin",
          headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html, application/json" },
        })
        if (res.ok || res.redirected) {
          this.close()
          // Refresh the in-place view so the edited entry shows new values.
          window.location.reload()
        } else {
          // Re-render the form (with errors) into the modal body.
          const html = await res.text()
          this.bodyTarget.innerHTML = html
          this.bindForm()
        }
      } catch {
        // Network failure: re-enable the button so the user can retry —
        // otherwise it stays disabled forever and the modal is wedged.
        if (submit) submit.disabled = false
        window.alert("Could not save — check your connection and try again.")
      }
    })

  }

  // Stimulus action wired by the Delete button in the modal form.
  async deleteEntry(event: Event): Promise<void> {
    const btn = event.currentTarget as HTMLElement
    const url = btn.dataset.url
    if (!url) return
    if (!window.confirm("Really delete this time entry?")) return
    const csrf = (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null)?.content || ""
    const res = await fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html", "X-CSRF-Token": csrf },
    })
    if (res.ok || res.redirected) {
      this.close()
      window.location.reload()
    } else {
      window.alert("Could not delete this entry.")
    }
  }
}
